import Image from 'next/image'
import React, { useEffect, useState } from 'react'

import { useIsMobileXL } from '@hooks/use-media-query'
import { NormalizedWrapper } from '@theme'
import { Description } from '@ui/sections/Description'

import {
  ExperienceSectionContainer,
  Container,
  Items,
  Subtitle,
  Title,
  Content,
  AdditionalImage,
  OrangeHexagonRightImage,
  OrangeHexagonLeftImage,
  WrapperIcon,
  GrayHexagonImage,
} from './styles'
import { AdvantagesSectionProps, AdvantagesSectionItems } from './types'

export function AdvantagesSection({
  items,
  title,
  subtitle,
  heightBlock,
  hover = false,
  hexagon = false,
  grayHexagon = false,
  titlePosition,
  textTransform,
  textPosition,
  contantPadding,
}: AdvantagesSectionProps) {
  const [hoveredId, setHoveredId] = useState<number | null>(0)
  const isMobile = useIsMobileXL()

  useEffect(() => {
    const handleScroll = () => {
      items.forEach(({ id }) => {
        const element = document.getElementById(`item-${id}`)
        if (isMobile) {
          if (element) {
            const rect = element.getBoundingClientRect()
            const isVisible = rect.top <= window.innerHeight - 170

            if (isVisible) {
              setHoveredId(id)
            }
          }
        }
      })
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [items, isMobile])

  const handleOnMouseOver = (id: number) => () => {
    setHoveredId(id)
  }

  const handleOnMouseLeave = () => {
    setHoveredId(null)
  }

  return (
    <NormalizedWrapper>
      <Description
        id={1}
        title={title}
        subtitle={subtitle}
        padding="0px"
        textPosition={textPosition}
        titlePosition={titlePosition}
        textTransform={textTransform}
      />

      <ExperienceSectionContainer padding={contantPadding}>
        {hexagon && (
          <>
            <OrangeHexagonLeftImage />
            <OrangeHexagonRightImage />
          </>
        )}
        {grayHexagon && <GrayHexagonImage />}
        <Container>
          {items.map(
            ({
              id,
              title,
              text,
              imagePath,
              imagePathHover,
              alt,
              imageTitle,
              imageWidth,
              imageHeight,
              className,
              additionalImages,
              imageClassName = '',
              icon,
              padding,
              titleWidth,
            }) => {
              const path = imagePath?.startsWith('/')
                ? imagePath
                : `/images/animated-icons/${imagePath}.svg`
              const hoverPath =
                imagePathHover ||
                `/images/animated-icons/${imagePath}-hover.svg`

              return (
                <Items
                  key={id}
                  id={`item-${id}`}
                  onMouseOver={handleOnMouseOver(id)}
                  onMouseLeave={handleOnMouseLeave}
                  heightBlock={heightBlock}
                  hover={hover}
                  imageClassName={imageClassName}
                  padding={padding}
                >
                  <Content
                    isVisible={hoveredId === id && hover}
                    imageClassName={imageClassName}
                  >
                    {icon ? (
                      <WrapperIcon $width={imageWidth}>{icon}</WrapperIcon>
                    ) : (
                      <Image
                        src={hoveredId === id && hover ? hoverPath : path}
                        alt={alt ?? title}
                        title={imageTitle ?? title}
                        width={imageWidth}
                        height={imageHeight}
                        className={className}
                      />
                    )}
                    <Title maxWidth={titleWidth} as="h3">
                      {title}
                    </Title>
                  </Content>
                  <Subtitle>{text}</Subtitle>
                  {additionalImages && (
                    <div>
                      {additionalImages.map(
                        ({
                          id: imageId,
                          imageComponent,
                          imageComponentHover,
                          options,
                        }: AdvantagesSectionItems) => (
                          <AdditionalImage {...options} key={imageId}>
                            {hoveredId === id
                              ? imageComponentHover
                              : imageComponent}
                          </AdditionalImage>
                        )
                      )}
                    </div>
                  )}
                </Items>
              )
            }
          )}
        </Container>
      </ExperienceSectionContainer>
    </NormalizedWrapper>
  )
}
